/**
 * One "Full name" field, and the two halves a reservation stores.
 *
 * Both booking sheets ask for the name once and split it on the first space —
 * the app has since 2026-09-05, the web sheet since 2026-09-10. The rules lived
 * only in `apps/mobile/src/shared/lib/full-name.ts`, which `@majourneys/booking`
 * cannot import, so they are here and both platforms read the same pair.
 */

/**
 * Is this a name we can hand to a provider?
 *
 * Owner, 2026-09-08: "user should not continue while entering only his first
 * name". A provider ringing a "sara" at the stables has nothing to check a
 * booking against, and `profileUpdateRequestSchema` refuses a single word for
 * the same reason.
 *
 * Two words, and the first one at least two letters. Deliberately no stricter:
 * an initial for the surname is a real answer ("Sara B"), non-Latin scripts
 * have no case to check, and a name is not a format.
 */
export function isFullName(value: string): boolean {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return parts.length >= 2 && (parts[0]?.length ?? 0) >= 2;
}

/**
 * The one field, split back into the two the reservation stores.
 *
 * Everything after the first word is the surname, so "Sara Ben Ali" keeps
 * "Ben Ali" whole rather than dropping the middle of it.
 */
export function splitFullName(value: string): { firstName: string; lastName: string } {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
}

/** The two stored halves, back into the one field the sheet shows. */
export function joinFullName(firstName: string, lastName: string): string {
  return [firstName, lastName].map((p) => p.trim()).filter(Boolean).join(" ");
}
